<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAdmin } from 'dashboard/composables/useAdmin';

// Eltafouk: «حملة رد على تم» admin-only dashboard.
//
// This is a standalone full-page iframe (NOT a per-conversation DashboardApp,
// so it does not go through Frame.vue). It embeds the platform campaigns page
// at api.eltafouk.com/app/campaigns and bridges the current agent's context to
// it two ways:
//   1. Query string — actor_id / actor_name / actor_email / role — so the page
//      knows who is acting and can gate its UI before any network call.
//   2. postMessage — the panelToken (the platform's panel API secret) is sent
//      ONLY in response to the iframe's `chatwoot-dashboard-app:fetch-info`
//      ping and ONLY to the trusted platform origin, exactly mirroring
//      Frame.vue. The token is never placed in the URL (would leak to history).
//
// Authorization is enforced in depth: the route meta.permissions gate, the
// isAdmin guard below, and — authoritatively — the platform re-validates
// actor_id against the Chatwoot Agents API server-side on every mutation.

const PANEL_ORIGIN = 'https://api.eltafouk.com';
const CAMPAIGNS_PATH = '/app/campaigns';

const store = useStore();
const { isAdmin } = useAdmin();
const currentUser = useMapGetter('getCurrentUser');

const iframeRef = ref(null);

const iframeSrc = computed(() => {
  const user = currentUser.value;
  if (!isAdmin.value || !user?.id) return null;
  const params = new URLSearchParams({
    actor_id: String(user.id),
    actor_name: user.name || '',
    actor_email: user.email || '',
    role: 'administrator',
  });
  return `${PANEL_ORIGIN}${CAMPAIGNS_PATH}?${params.toString()}`;
});

// Respond to the iframe's fetch-info ping with the panel token, scoped to the
// trusted platform origin only (same contract as Frame.vue's onIframeLoad).
const handleMessage = async event => {
  if (event.data !== 'chatwoot-dashboard-app:fetch-info') return;
  const frame = iframeRef.value;
  if (!frame || !frame.contentWindow) return;
  let panelToken = store.getters['panelToken/getPanelToken'];
  if (!panelToken) {
    // Cold load (admin opened this page directly, without visiting a conversation
    // that warms the token): fetch it before answering so the iframe's API calls
    // carry a valid X-Panel-Token instead of getting 401'd.
    try {
      await store.dispatch('panelToken/fetchPanelToken');
      panelToken = store.getters['panelToken/getPanelToken'];
    } catch {
      panelToken = null;
    }
  }
  frame.contentWindow.postMessage(
    JSON.stringify({ event: 'appContext', data: {}, panelToken }),
    PANEL_ORIGIN
  );
};

onMounted(() => {
  // Warm the panel token so it is ready when the iframe pings for it.
  store.dispatch('panelToken/fetchPanelToken').catch(() => {});
  window.addEventListener('message', handleMessage);
});
onBeforeUnmount(() => window.removeEventListener('message', handleMessage));
</script>

<template>
  <div class="flex flex-col flex-1 w-full h-full bg-n-surface-1">
    <div
      v-if="!isAdmin"
      class="flex items-center justify-center flex-1 text-n-slate-11"
    >
      هذه الصفحة للمدراء فقط
    </div>
    <iframe
      v-else-if="iframeSrc"
      ref="iframeRef"
      :src="iframeSrc"
      class="flex-1 w-full border-0"
      allow="clipboard-write"
      title="حملة رد على تم"
    />
  </div>
</template>
